import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  GraduationCap, ShoppingBag, Receipt, AlertTriangle,
  TrendingUp, Users, CreditCard, Banknote, Package
} from 'lucide-react';

interface TrainingOverview {
  trainingOrders: number;
  trainingTotal: number;
  regularOrders: number;
  regularTotal: number;
  totalOrders: number;
  totalAmount: number;
  trainingPercentageOrders: number;
  trainingPercentageAmount: number;
  avgTrainingOrderValue: number;
  totalVatLost: number;
}

interface VatRow {
  vatRate: number;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
}

interface CashierRow {
  cashierName: string;
  orders: number;
  total: number;
}

interface PaymentRow {
  paymentType: string;
  orders: number;
  total: number;
}

interface ProductRow {
  productName: string;
  quantity: number;
  total: number;
  vatRate: number;
}

interface TrainingData {
  overview: TrainingOverview;
  vatBreakdown: VatRow[];
  byCashier: CashierRow[];
  byPaymentType: PaymentRow[];
  byProduct: ProductRow[];
}

interface Props {
  data: TrainingData | null;
  loading: boolean;
}

const fmt = (n: number) =>
  new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(n);

const pct = (n: number) => `${n.toFixed(1)}%`;

const TrainingAnalytics: React.FC<Props> = ({ data, loading }) => {
  const { t } = useTranslation(['analytics', 'common']);

  const paymentLabel: Record<string, string> = {
    cash: t('analytics:staffSummary.paymentCash'),
    card: t('analytics:customer.paymentCard'),
    unknown: t('analytics:customer.paymentUnknown'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data || data.overview.trainingOrders === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <GraduationCap className="h-14 w-14 text-t4 mb-4" />
        <p className="text-t3 font-medium text-lg">{t('analytics:training.noTrainingOrders')}</p>
        <p className="text-t4 text-sm mt-1">{t('analytics:training.forSelectedPeriod')}</p>
      </div>
    );
  }

  const ov = data.overview;

  return (
    <div className="space-y-5">

      {/* Overview KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Training orders */}
        <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 rounded-2xl border border-orange-500/20 p-4">
          <p className="text-xs font-medium text-t3 mb-1">{t('analytics:training.trainingOrders')}</p>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{ov.trainingOrders}</p>
          <p className="text-xs text-t4 mt-1">{t('analytics:training.ofTotal', { pct: pct(ov.trainingPercentageOrders) })}</p>
        </div>

        {/* Training total */}
        <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-2xl border border-amber-500/20 p-4">
          <p className="text-xs font-medium text-t3 mb-1">{t('analytics:training.trainingAmount')}</p>
          <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">{fmt(ov.trainingTotal)}</p>
          <p className="text-xs text-t4 mt-1">{t('analytics:training.ofRevenue', { pct: pct(ov.trainingPercentageAmount) })}</p>
        </div>

        {/* Avg order value */}
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-2xl border border-blue-500/20 p-4">
          <p className="text-xs font-medium text-t3 mb-1">{t('analytics:training.avgTrainingOrder')}</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{fmt(ov.avgTrainingOrderValue)}</p>
          <p className="text-xs text-t4 mt-1">{t('analytics:training.perOrder')}</p>
        </div>

        {/* VAT lost */}
        <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 rounded-2xl border border-red-500/20 p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-t3 mb-1">{t('analytics:training.vatNotIssued')}</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{fmt(ov.totalVatLost)}</p>
              <p className="text-xs text-t4 mt-1">{t('analytics:training.ifReceiptsIssued')}</p>
            </div>
            <AlertTriangle className="h-5 w-5 text-red-600/60 dark:text-red-400/60 shrink-0 mt-0.5" />
          </div>
        </div>
      </div>

      {/* Training vs Regular comparison */}
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line p-5">
        <h3 className="text-sm font-semibold text-t1 mb-4 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-t3" />
          {t('analytics:training.regularVsTraining')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* By order count */}
          <div>
            <p className="text-xs text-t3 mb-2">{t('analytics:training.orderCount')}</p>
            <div className="space-y-2">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-t2">{t('analytics:training.regular')}</span>
                  <span className="text-t2 font-medium">{ov.regularOrders} ({pct(ov.totalOrders > 0 ? (ov.regularOrders / ov.totalOrders) * 100 : 0)})</span>
                </div>
                <div className="w-full bg-surface-2 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${ov.totalOrders > 0 ? (ov.regularOrders / ov.totalOrders) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-orange-700 dark:text-orange-300">{t('analytics:training.trainingLabel')}</span>
                  <span className="text-orange-700 dark:text-orange-300 font-medium">{ov.trainingOrders} ({pct(ov.trainingPercentageOrders)})</span>
                </div>
                <div className="w-full bg-surface-2 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-orange-500 transition-all duration-500"
                    style={{ width: `${ov.trainingPercentageOrders}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* By revenue */}
          <div>
            <p className="text-xs text-t3 mb-2">{t('analytics:training.amountEuro')}</p>
            <div className="space-y-2">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-t2">{t('analytics:training.regular')}</span>
                  <span className="text-t2 font-medium">{fmt(ov.regularTotal)} ({pct(ov.totalAmount > 0 ? (ov.regularTotal / ov.totalAmount) * 100 : 0)})</span>
                </div>
                <div className="w-full bg-surface-2 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${ov.totalAmount > 0 ? (ov.regularTotal / ov.totalAmount) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-orange-700 dark:text-orange-300">{t('analytics:training.trainingLabel')}</span>
                  <span className="text-orange-700 dark:text-orange-300 font-medium">{fmt(ov.trainingTotal)} ({pct(ov.trainingPercentageAmount)})</span>
                </div>
                <div className="w-full bg-surface-2 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-orange-500 transition-all duration-500"
                    style={{ width: `${ov.trainingPercentageAmount}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* VAT Breakdown */}
      {data.vatBreakdown.length > 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="px-5 py-4 border-b border-line flex items-center justify-between">
            <h3 className="text-sm font-semibold text-t1 flex items-center gap-2">
              <Receipt className="h-4 w-4 text-t3" />
              {t('analytics:training.vatAnalysis')}
            </h3>
            <span className="text-xs bg-red-500/20 text-red-700 dark:text-red-300 px-2 py-1 rounded-lg font-medium">
              {t('analytics:training.totalVat', { amount: fmt(ov.totalVatLost) })}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2 dark:bg-surface-2/30">
                  <th className="text-left px-5 py-3 text-xs font-medium text-t3">{t('analytics:training.vatRateColumn')}</th>
                  <th className="text-right px-5 py-3 text-xs font-medium text-t3">{t('analytics:training.netValueColumn')}</th>
                  <th className="text-right px-5 py-3 text-xs font-medium text-t3">{t('analytics:training.vatColumn')}</th>
                  <th className="text-right px-5 py-3 text-xs font-medium text-t3">{t('analytics:grandTotal')}</th>
                  <th className="text-right px-5 py-3 text-xs font-medium text-t3">{t('analytics:training.vatPercentColumn')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/50">
                {data.vatBreakdown.map(row => (
                  <tr key={row.vatRate} className="hover:bg-surface-3 dark:hover:bg-surface-2/20 transition-colors">
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-500/20 text-blue-700 dark:text-blue-300">
                        {row.vatRate}%
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-t2">{fmt(row.netAmount)}</td>
                    <td className="px-5 py-3 text-right font-medium text-red-700 dark:text-red-300">{fmt(row.vatAmount)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-t1">{fmt(row.grossAmount)}</td>
                    <td className="px-5 py-3 text-right text-t3">
                      {row.grossAmount > 0 ? pct((row.vatAmount / row.grossAmount) * 100) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-line-strong bg-surface-2 dark:bg-surface-2/20">
                <tr>
                  <td className="px-5 py-3 text-xs font-semibold text-t2">{t('analytics:grandTotal')}</td>
                  <td className="px-5 py-3 text-right text-sm font-semibold text-t2">
                    {fmt(data.vatBreakdown.reduce((s, r) => s + r.netAmount, 0))}
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-bold text-red-700 dark:text-red-300">
                    {fmt(ov.totalVatLost)}
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-bold text-t1">
                    {fmt(ov.trainingTotal)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* By Cashier + By Payment Type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* By Cashier */}
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="px-5 py-4 border-b border-line flex items-center gap-2">
            <Users className="h-4 w-4 text-t3" />
            <h3 className="text-sm font-semibold text-t1">{t('analytics:training.byCashier')}</h3>
          </div>
          <div className="p-4 space-y-3">
            {data.byCashier.map(c => {
              const pctAmt = ov.trainingTotal > 0 ? (c.total / ov.trainingTotal) * 100 : 0;
              return (
                <div key={c.cashierName}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-t2 truncate max-w-[140px]">{c.cashierName}</span>
                    <span className="text-t2 font-medium shrink-0 ml-2">
                      {t('analytics:training.ordersAndAmount', { orders: c.orders, amount: fmt(c.total) })}
                    </span>
                  </div>
                  <div className="w-full bg-surface-2 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full bg-orange-500 transition-all duration-500"
                      style={{ width: `${pctAmt}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* By Payment Type */}
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="px-5 py-4 border-b border-line flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-t3" />
            <h3 className="text-sm font-semibold text-t1">{t('analytics:training.paymentMethod')}</h3>
          </div>
          <div className="p-4 space-y-3">
            {data.byPaymentType.map(p => {
              const pctAmt = ov.trainingTotal > 0 ? (p.total / ov.trainingTotal) * 100 : 0;
              const Icon = p.paymentType === 'cash' ? Banknote : CreditCard;
              const color = p.paymentType === 'cash' ? 'bg-emerald-500' : 'bg-blue-500';
              const textColor = p.paymentType === 'cash' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400';
              return (
                <div key={p.paymentType}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={`flex items-center gap-1.5 font-medium ${textColor}`}>
                      <Icon className="h-3.5 w-3.5" />
                      {paymentLabel[p.paymentType] || p.paymentType}
                    </span>
                    <span className="text-t2">
                      {t('analytics:training.ordersAndAmount', { orders: p.orders, amount: fmt(p.total) })}
                    </span>
                  </div>
                  <div className="w-full bg-surface-2 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${color} transition-all duration-500`}
                      style={{ width: `${pctAmt}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top Products */}
      {data.byProduct.length > 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="px-5 py-4 border-b border-line flex items-center gap-2">
            <Package className="h-4 w-4 text-t3" />
            <h3 className="text-sm font-semibold text-t1">{t('analytics:training.productsInTraining')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2 dark:bg-surface-2/30">
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-t3">{t('analytics:training.productColumn')}</th>
                  <th className="text-right px-5 py-2.5 text-xs font-medium text-t3">{t('analytics:training.quantityColumn')}</th>
                  <th className="text-right px-5 py-2.5 text-xs font-medium text-t3">{t('analytics:grandTotal')}</th>
                  <th className="text-right px-5 py-2.5 text-xs font-medium text-t3">{t('analytics:training.vatColumn')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/50">
                {data.byProduct.map((p, i) => (
                  <tr key={p.productName} className="hover:bg-surface-3 dark:hover:bg-surface-2/20 transition-colors">
                    <td className="px-5 py-2.5 text-t2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-t4 w-5 text-right">{i + 1}.</span>
                        <span className="truncate max-w-[160px]" title={p.productName}>{p.productName}</span>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-right text-t3">{p.quantity}</td>
                    <td className="px-5 py-2.5 text-right font-medium text-t1">{fmt(p.total)}</td>
                    <td className="px-5 py-2.5 text-right">
                      <span className="text-xs bg-blue-500/20 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">{p.vatRate}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default TrainingAnalytics;
